import { useState, useEffect } from 'react';
import {
    ArrowUpCircle, CheckCircle2, XCircle, Zap, Users, Briefcase,
    Calendar, Clock, FileText, TrendingUp, Package, ChevronRight,
    AlertTriangle, Shield, Sparkles,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { getMyPlan, getMyUsageHistory } from '@/services/apiClient';
import UpgradeRequestModal from '@/components/admin/UpgradeRequestModal';
import MyInvoicesPanel from '@/components/admin/MyInvoicesPanel';
import MyBillingPanel from '@/components/admin/MyBillingPanel';
import AddonRequestModal from '@/components/admin/AddonRequestModal';
import UsageHistoryTable from '@/components/billing/UsageHistoryTable';

// ─── helpers ────────────────────────────────────────────────────────────────

function formatDate(d) {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

function daysUntil(dateStr) {
    if (!dateStr) return null;
    return Math.max(0, Math.ceil((new Date(dateStr) - Date.now()) / 86400000));
}

function formatLimitValue(value) {
    if (value === -1) return 'Unlimited';
    if (value === null || value === undefined) return '—';
    return String(value);
}

const CAPABILITY_ITEMS = [
    { key: 'hasProctoring', label: 'AI Proctoring', highlight: true },
    { key: 'hasAdvancedAnalytics', label: 'Advanced Analytics', highlight: true },
    { key: 'hasCustomJDTemplates', label: 'Custom Job Descriptions' },
    { key: 'hasBrandingOnReports', label: 'Branding on Reports' },
    { key: 'hasWhiteLabelReports', label: 'White-label Reports', highlight: true },
    { key: 'hasDedicatedManager', label: 'Dedicated Account Manager', highlight: true },
    { key: 'hasSLASupport', label: 'SLA Support', highlight: true },
    { key: 'hasAPIAccess', label: 'API Access' },
    { key: 'hasRoleBasedAccess', label: 'Role-based Access Control' },
];

// ─── circular gauge ──────────────────────────────────────────────────────────

function CircleGauge({ used, limit, unlimited, size = 80, strokeWidth = 7, label, sublabel, urgency }) {
    const r = (size - strokeWidth) / 2;
    const circ = 2 * Math.PI * r;
    const pct = unlimited || !limit ? 0 : Math.min(100, (used / limit) * 100);
    const dash = circ - (pct / 100) * circ;

    const trackColor = 'stroke-muted';
    const fillColor = urgency === 'danger'
        ? 'stroke-destructive'
        : urgency === 'warning'
        ? 'stroke-amber-500'
        : 'stroke-primary';

    return (
        <div className="flex flex-col items-center gap-1.5">
            <div className="relative" style={{ width: size, height: size }}>
                <svg width={size} height={size} className="-rotate-90">
                    <circle
                        cx={size / 2} cy={size / 2} r={r}
                        fill="none" strokeWidth={strokeWidth}
                        className={trackColor}
                    />
                    {!unlimited && limit > 0 && (
                        <circle
                            cx={size / 2} cy={size / 2} r={r}
                            fill="none" strokeWidth={strokeWidth}
                            strokeDasharray={circ}
                            strokeDashoffset={dash}
                            strokeLinecap="round"
                            className={`${fillColor} transition-all duration-700`}
                        />
                    )}
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className="text-base font-bold tabular-nums text-foreground leading-none">
                        {unlimited ? '∞' : used}
                    </span>
                    {!unlimited && limit > 0 && (
                        <span className="text-[9px] text-muted-foreground leading-none mt-0.5">of {limit}</span>
                    )}
                </div>
            </div>
            <p className="text-xs font-medium text-foreground text-center">{label}</p>
            {sublabel && <p className="text-[10px] text-muted-foreground text-center">{sublabel}</p>}
        </div>
    );
}

// ─── countdown ring ───────────────────────────────────────────────────────────

function CountdownRing({ daysLeft, totalDays, status }) {
    const size = 120;
    const stroke = 9;
    const r = (size - stroke) / 2;
    const circ = 2 * Math.PI * r;
    const pct = totalDays > 0 ? Math.max(0, Math.min(1, daysLeft / totalDays)) : 0;
    const dash = circ - pct * circ;
    const urgent = daysLeft <= 3;
    const fillClass = urgent ? 'stroke-destructive' : status === 'grace_period' ? 'stroke-amber-500' : 'stroke-blue-500';

    return (
        <div className="relative" style={{ width: size, height: size }}>
            <svg width={size} height={size} className="-rotate-90">
                <circle cx={size / 2} cy={size / 2} r={r} fill="none" strokeWidth={stroke} className="stroke-muted" />
                <circle
                    cx={size / 2} cy={size / 2} r={r} fill="none" strokeWidth={stroke}
                    strokeDasharray={circ} strokeDashoffset={dash} strokeLinecap="round"
                    className={`${fillClass} transition-all duration-700`}
                />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className={`text-2xl font-bold tabular-nums ${urgent ? 'text-destructive' : 'text-foreground'}`}>
                    {daysLeft}
                </span>
                <span className="text-[10px] text-muted-foreground">days left</span>
            </div>
        </div>
    );
}

// ─── usage row ────────────────────────────────────────────────────────────────

function UsageRow({ icon: Icon, label, used, limit, unlimited, addon = 0, onTopUp }) {
    const effective = unlimited ? null : (limit || 0) + addon;
    const pct = effective ? Math.min(100, Math.round((used / effective) * 100)) : 0;
    const urgency = pct >= 90 ? 'danger' : pct >= 70 ? 'warning' : 'ok';
    const barColor = urgency === 'danger' ? 'bg-destructive' : urgency === 'warning' ? 'bg-amber-500' : 'bg-primary';
    const remaining = effective ? Math.max(0, effective - used) : null;

    return (
        <div className="space-y-2 rounded-lg border border-border bg-muted/20 p-4">
            <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                    <div className="flex h-7 w-7 items-center justify-center rounded-md bg-primary/10">
                        <Icon className="h-3.5 w-3.5 text-primary" />
                    </div>
                    <span className="text-sm font-medium text-foreground">{label}</span>
                    {addon > 0 && (
                        <span className="rounded-full bg-primary/10 px-1.5 py-0.5 text-[10px] font-medium text-primary">
                            +{addon} add-on
                        </span>
                    )}
                </div>
                <div className="text-right">
                    <span className="text-sm font-bold tabular-nums text-foreground">
                        {unlimited ? `${used}` : `${used} / ${effective}`}
                    </span>
                    {unlimited && <span className="text-xs text-muted-foreground"> / ∞</span>}
                </div>
            </div>

            {!unlimited && effective > 0 && (
                <>
                    <div className="relative h-2 w-full overflow-hidden rounded-full bg-muted">
                        <div
                            className={`h-full rounded-full transition-all duration-700 ${barColor}`}
                            style={{ width: `${pct}%` }}
                        />
                        {addon > 0 && limit > 0 && (
                            <div
                                className="absolute inset-y-0 w-px bg-primary/40"
                                style={{ left: `${Math.round((limit / effective) * 100)}%` }}
                            />
                        )}
                    </div>
                    <div className="flex items-center justify-between">
                        <span className={`text-xs ${urgency === 'danger' ? 'text-destructive font-medium' : urgency === 'warning' ? 'text-amber-500' : 'text-muted-foreground'}`}>
                            {urgency === 'danger' ? '⚠ Almost at limit' : urgency === 'warning' ? 'Approaching limit' : `${remaining} remaining`}
                        </span>
                        {urgency !== 'ok' && onTopUp && (
                            <button
                                type="button"
                                onClick={onTopUp}
                                className="flex items-center gap-1 text-[11px] font-medium text-primary hover:underline"
                            >
                                <Package className="h-3 w-3" /> Get add-on
                            </button>
                        )}
                    </div>
                </>
            )}
        </div>
    );
}

// ─── plan details (description, features, limits) ───────────────────────────

function PlanDetailsSection({ plan }) {
    const limits = plan?.limits || {};

    return (
        <section className="rounded-xl border border-border bg-card overflow-hidden">
            <div className="border-b border-border/60 bg-muted/20 px-5 py-3">
                <h2 className="text-sm font-semibold text-foreground flex items-center gap-2">
                    <FileText className="h-4 w-4 text-primary" aria-hidden />
                    Plan details
                </h2>
                <p className="text-xs text-muted-foreground mt-0.5">What your current subscription includes</p>
            </div>

            <div className="p-5 space-y-5">
                {plan.description && (
                    <p className="text-sm leading-relaxed text-foreground">{plan.description}</p>
                )}
                {plan.idealFor && (
                    <p className="text-xs text-muted-foreground">
                        <span className="font-semibold text-foreground">Ideal for: </span>
                        {plan.idealFor}
                    </p>
                )}

                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                    {[
                        { label: 'AI interviews / month', value: formatLimitValue(limits.maxInterviewsPerMonth) },
                        { label: 'Active jobs', value: formatLimitValue(limits.maxActiveJobs) },
                        { label: 'Team members', value: formatLimitValue(limits.maxUsers) },
                    ].map(({ label, value }) => (
                        <div key={label} className="rounded-lg border border-border/80 bg-muted/25 px-4 py-3 text-center">
                            <p className="text-xl font-bold tabular-nums text-foreground">{value}</p>
                            <p className="mt-1 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                                {label}
                            </p>
                        </div>
                    ))}
                </div>

                {plan.features?.length > 0 && (
                    <div>
                        <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-primary">
                            Included in your plan
                        </p>
                        <ul className="grid gap-1.5 sm:grid-cols-2">
                            {plan.features.map((feature, i) => (
                                <li
                                    key={i}
                                    className="flex items-start gap-2 rounded-md bg-muted/30 px-3 py-2 text-sm text-foreground"
                                >
                                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden />
                                    <span>{feature}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                <div>
                    <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        Platform capabilities
                    </p>
                    <div className="grid gap-1.5 sm:grid-cols-2">
                        {CAPABILITY_ITEMS.map(({ key, label, highlight }) => (
                            <FeatureItem
                                key={key}
                                label={label}
                                enabled={Boolean(limits[key])}
                                highlight={highlight}
                            />
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}

// ─── feature badge ────────────────────────────────────────────────────────────

function FeatureItem({ label, enabled, highlight }) {
    return (
        <div className={`flex items-center gap-3 rounded-lg px-3 py-2.5 ${enabled ? highlight ? 'bg-primary/8 border border-primary/20' : 'bg-muted/30' : 'opacity-40'}`}>
            {enabled
                ? <CheckCircle2 className={`h-4 w-4 shrink-0 ${highlight ? 'text-primary' : 'text-green-500'}`} />
                : <XCircle className="h-4 w-4 shrink-0 text-muted-foreground" />}
            <span className={`text-sm ${enabled ? 'text-foreground' : 'text-muted-foreground'}`}>{label}</span>
            {highlight && enabled && <Sparkles className="ml-auto h-3.5 w-3.5 text-primary opacity-60" />}
        </div>
    );
}

// ─── status config ────────────────────────────────────────────────────────────

const STATUS_CONFIG = {
    active: {
        label: 'Active',
        badgeCls: 'bg-green-500/15 text-green-600 border-green-500/30',
        heroCls: 'from-green-500/8 to-transparent',
        icon: CheckCircle2,
        iconCls: 'text-green-500',
    },
    trial: {
        label: 'Trial',
        badgeCls: 'bg-blue-500/15 text-blue-600 border-blue-500/30',
        heroCls: 'from-blue-500/8 to-transparent',
        icon: Clock,
        iconCls: 'text-blue-500',
    },
    grace_period: {
        label: 'Grace Period',
        badgeCls: 'bg-amber-500/15 text-amber-600 border-amber-500/30',
        heroCls: 'from-amber-500/8 to-transparent',
        icon: AlertTriangle,
        iconCls: 'text-amber-500',
    },
    expired: {
        label: 'Expired',
        badgeCls: 'bg-destructive/15 text-destructive border-destructive/30',
        heroCls: 'from-destructive/8 to-transparent',
        icon: XCircle,
        iconCls: 'text-destructive',
    },
    cancelled: {
        label: 'Cancelled',
        badgeCls: 'bg-muted text-muted-foreground border-border',
        heroCls: 'from-muted/30 to-transparent',
        icon: XCircle,
        iconCls: 'text-muted-foreground',
    },
};

const ACTION_META = {
    activated: { label: 'Plan Activated', cls: 'bg-green-500/10 text-green-500' },
    upgraded: { label: 'Upgraded', cls: 'bg-primary/10 text-primary' },
    downgraded: { label: 'Downgraded', cls: 'bg-amber-500/10 text-amber-500' },
    cancelled: { label: 'Cancelled', cls: 'bg-destructive/10 text-destructive' },
    trial_extended: { label: 'Trial Extended', cls: 'bg-blue-500/10 text-blue-500' },
    reactivated: { label: 'Reactivated', cls: 'bg-green-500/10 text-green-500' },
    assigned: { label: 'Plan Assigned', cls: 'bg-muted text-muted-foreground' },
};

// ─── main page ────────────────────────────────────────────────────────────────

export default function AdminMyPlanPage() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [upgradeOpen, setUpgradeOpen] = useState(false);
    const [addonOpen, setAddonOpen] = useState(false);

    useEffect(() => {
        getMyPlan()
            .then(res => setData(res.data))
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    if (loading) {
        return (
            <div className="mx-auto  space-y-4 animate-pulse">
                <div className="h-48 rounded-2xl bg-muted" />
                <div className="grid grid-cols-3 gap-3">
                    {[1, 2, 3].map(i => <div key={i} className="h-28 rounded-xl bg-muted" />)}
                </div>
                <div className="h-64 rounded-xl bg-muted" />
            </div>
        );
    }

    if (!data) {
        return (
            <div className="flex h-64 items-center justify-center text-muted-foreground">
                Unable to load plan information.
            </div>
        );
    }

    const { company, plan, usage, limits: effectiveLimits, subscriptionLogs, billing } = data;
    const amountDue = Number(billing?.amountDue) || 0;
    const sub = company?.subscription;
    const status = sub?.status || 'cancelled';
    const sc = STATUS_CONFIG[status] || STATUS_CONFIG.cancelled;
    const StatusIcon = sc.icon;
    const planLimits = plan?.limits || {};

    const trialDaysLeft = status === 'trial' ? daysUntil(sub?.trialEndDate) : null;
    const graceDaysLeft = status === 'grace_period' ? daysUntil(sub?.gracePeriodEndDate) : null;
    const countdownDays = trialDaysLeft ?? graceDaysLeft;
    const totalDays = status === 'trial' ? 14 : status === 'grace_period' ? (sub?.gracePeriodDays || 7) : 0;

    const interviewsUsed = usage?.interviewsUsedThisMonth || 0;
    const interviewLimit = effectiveLimits?.maxInterviewsPerMonth;
    const interviewPct = interviewLimit ? Math.min(100, Math.round((interviewsUsed / interviewLimit) * 100)) : 0;
    const jobsUsed = usage?.activeJobs || 0;
    const jobLimit = effectiveLimits?.maxActiveJobs;
    const membersUsed = usage?.teamMembers || 0;
    const memberLimit = effectiveLimits?.maxUsers;

    const interviewUrgency = interviewPct >= 90 ? 'danger' : interviewPct >= 70 ? 'warning' : 'ok';
    const jobUrgency = jobLimit && !effectiveLimits?.unlimited?.activeJobs
        ? (jobsUsed / jobLimit >= 0.9 ? 'danger' : jobsUsed / jobLimit >= 0.7 ? 'warning' : 'ok')
        : 'ok';

    const hasAddon = (usage?.addonInterviewCredits || 0) > 0 || (usage?.addonActiveJobCredits || 0) > 0;

    return (
        <div className="mx-auto  space-y-5">

            {/* ── Hero Plan Card ────────────────────────────────────────── */}
            <div className={`relative overflow-hidden rounded-2xl border border-border bg-card`}>
                <div className={`absolute inset-0 bg-gradient-to-br ${sc.heroCls} pointer-events-none`} />
                <div className="relative p-6">
                    <div className="flex flex-wrap items-start justify-between gap-4">
                        {/* left: plan info */}
                        <div className="space-y-2 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                                <StatusIcon className={`h-4 w-4 shrink-0 ${sc.iconCls}`} />
                                <Badge variant="outline" className={`text-xs ${sc.badgeCls}`}>{sc.label}</Badge>
                                {countdownDays !== null && (
                                    <Badge variant="outline" className={`text-xs ${countdownDays <= 3 ? 'border-destructive/40 text-destructive bg-destructive/10' : 'border-amber-500/40 text-amber-600 bg-amber-500/10'}`}>
                                        {countdownDays}d remaining
                                    </Badge>
                                )}
                            </div>
                            <h1 className="text-2xl font-bold text-foreground">
                                {plan?.name || 'No Plan Assigned'}
                            </h1>
                            <p className="text-sm text-muted-foreground">{company?.companyName}</p>
                            {plan && (
                                <p className="text-3xl font-bold text-foreground tabular-nums">
                                    ₹{plan.price.toLocaleString('en-IN')}
                                    <span className="text-sm font-normal text-muted-foreground">/month</span>
                                </p>
                            )}

                            {/* date meta */}
                            <div className="flex flex-wrap gap-4 pt-1 text-xs text-muted-foreground">
                                {status === 'trial' && (
                                    <>
                                        <span>Trial started {formatDate(sub?.trialStartDate)}</span>
                                        <span className={countdownDays !== null && countdownDays <= 3 ? 'text-destructive font-medium' : ''}>
                                            Ends {formatDate(sub?.trialEndDate)}
                                        </span>
                                    </>
                                )}
                                {status === 'active' && (
                                    <>
                                        <span>Active since {formatDate(sub?.currentPeriodStart)}</span>
                                        {sub?.currentPeriodEnd && <span>Renews {formatDate(sub.currentPeriodEnd)}</span>}
                                    </>
                                )}
                                {status === 'grace_period' && (
                                    <span className="text-amber-500 font-medium">
                                        Grace period ends {formatDate(sub?.gracePeriodEndDate)}
                                    </span>
                                )}
                                {hasAddon && (
                                    <span className="text-primary font-medium">
                                        Add-on active: {usage?.addonInterviewCredits > 0 ? `+${usage.addonInterviewCredits} interviews` : ''}{usage?.addonActiveJobCredits > 0 ? ` +${usage.addonActiveJobCredits} jobs` : ''}
                                    </span>
                                )}
                            </div>
                        </div>

                        {/* right: countdown ring or zap icon */}
                        <div className="flex flex-col items-end gap-3">
                            {countdownDays !== null ? (
                                <CountdownRing daysLeft={countdownDays} totalDays={totalDays} status={status} />
                            ) : (
                                <div className={`flex h-16 w-16 items-center justify-center rounded-2xl ${status === 'active' ? 'bg-green-500/10' : 'bg-muted'}`}>
                                    <Zap className={`h-8 w-8 ${status === 'active' ? 'text-green-500' : 'text-muted-foreground'}`} />
                                </div>
                            )}

                            {/* action buttons */}
                            <div className="flex gap-2">
                                <Button size="sm" variant="outline" onClick={() => setAddonOpen(true)} className="gap-1.5 text-xs">
                                    <Package className="h-3.5 w-3.5" /> Add-On
                                </Button>
                                <Button size="sm" onClick={() => setUpgradeOpen(true)} className="gap-1.5 text-xs">
                                    <ArrowUpCircle className="h-3.5 w-3.5" /> Upgrade
                                </Button>
                            </div>
                        </div>
                    </div>

                    {/* status alert bar */}
                    {(status === 'grace_period' || status === 'expired' || status === 'cancelled') && (
                        <div className={`mt-4 flex items-center gap-2 rounded-lg px-3 py-2.5 text-sm font-medium
                            ${status === 'expired' || status === 'cancelled'
                                ? 'bg-destructive/10 text-destructive'
                                : 'bg-amber-500/10 text-amber-600'}`}>
                            <AlertTriangle className="h-4 w-4 shrink-0" />
                            {status === 'grace_period'
                                ? amountDue > 0
                                    // Naming the amount and the date is the difference between
                                    // the company knowing what to do and only knowing something
                                    // is wrong.
                                    ? `Rs. ${amountDue.toLocaleString('en-IN')} is outstanding. Clear it by ${formatDate(sub?.gracePeriodEndDate)} to avoid interruption of service — after that, new job descriptions and interview scheduling will stop. Interviews already scheduled will still run.`
                                    : `Your account is in a grace period. All features remain available for ${graceDaysLeft} more day${graceDaysLeft !== 1 ? 's' : ''}. Contact your account manager to continue.`
                                : amountDue > 0
                                    ? `Your subscription has ended and Rs. ${amountDue.toLocaleString('en-IN')} is outstanding. Clear the balance to restore full access — your existing data stays visible.`
                                    : 'Your subscription has ended. Actions are currently blocked. Contact your account manager to reactivate.'}
                            <Button
                                size="sm" variant="ghost"
                                className="ml-auto h-7 shrink-0 text-xs underline-offset-2 hover:underline"
                                onClick={() => setUpgradeOpen(true)}
                            >
                                Request upgrade <ChevronRight className="ml-1 h-3 w-3" />
                            </Button>
                        </div>
                    )}
                </div>
            </div>

            {plan && <PlanDetailsSection plan={plan} />}

            {/* ── Circular Usage Summary ────────────────────────────────── */}
            <div className="grid grid-cols-3 gap-3">
                <div className="rounded-xl border border-border bg-card p-4 flex flex-col items-center gap-3">
                    <CircleGauge
                        used={interviewsUsed}
                        limit={interviewLimit}
                        unlimited={effectiveLimits?.unlimited?.interviews}
                        urgency={interviewUrgency}
                        label="Interviews"
                        sublabel="this month"
                    />
                    <div className="text-center space-y-0.5">
                        <p className={`text-xs font-semibold ${interviewUrgency === 'danger' ? 'text-destructive' : interviewUrgency === 'warning' ? 'text-amber-500' : 'text-green-500'}`}>
                            {effectiveLimits?.unlimited?.interviews ? 'Unlimited' : `${usage?.interviewsRemaining ?? 0} left`}
                        </p>
                        <p className="text-[10px] text-muted-foreground">
                            {usage?.completedSessionsThisMonth ?? 0} completed
                        </p>
                    </div>
                </div>

                <div className="rounded-xl border border-border bg-card p-4 flex flex-col items-center gap-3">
                    <CircleGauge
                        used={jobsUsed}
                        limit={jobLimit}
                        unlimited={effectiveLimits?.unlimited?.activeJobs}
                        urgency={jobUrgency}
                        label="Active Jobs"
                        sublabel="open positions"
                    />
                    <div className="text-center space-y-0.5">
                        <p className={`text-xs font-semibold ${jobUrgency === 'danger' ? 'text-destructive' : jobUrgency === 'warning' ? 'text-amber-500' : 'text-green-500'}`}>
                            {effectiveLimits?.unlimited?.activeJobs ? 'Unlimited' : `${usage?.activeJobsRemaining ?? 0} slots left`}
                        </p>
                        <p className="text-[10px] text-muted-foreground">
                            {usage?.scheduledSessions ?? 0} sessions pending
                        </p>
                    </div>
                </div>

                <div className="rounded-xl border border-border bg-card p-4 flex flex-col items-center gap-3">
                    <CircleGauge
                        used={membersUsed}
                        limit={memberLimit}
                        unlimited={effectiveLimits?.unlimited?.users}
                        urgency={memberLimit && !effectiveLimits?.unlimited?.users && membersUsed / memberLimit >= 0.9 ? 'danger' : 'ok'}
                        label="Team Members"
                        sublabel="non-candidate"
                    />
                    <div className="text-center space-y-0.5">
                        <p className="text-xs font-semibold text-green-500">
                            {effectiveLimits?.unlimited?.users ? 'Unlimited' : `${usage?.teamMembersRemaining ?? 0} seats left`}
                        </p>
                        <p className="text-[10px] text-muted-foreground">across all roles</p>
                    </div>
                </div>
            </div>

            {/* ── Tabs: Usage | Features | History ─────────────────────── */}
            <Tabs defaultValue="usage">
                <TabsList className="w-full">
                    <TabsTrigger value="usage" className="flex-1">Usage Details</TabsTrigger>
                    <TabsTrigger value="features" className="flex-1">Plan Features</TabsTrigger>
                    <TabsTrigger value="invoices" className="flex-1">Invoices</TabsTrigger>
                    <TabsTrigger value="history" className="flex-1">History</TabsTrigger>
                </TabsList>

                {/* ── Usage Tab ── */}
                <TabsContent value="usage" className="mt-4 space-y-3">
                    <UsageRow
                        icon={Calendar}
                        label="AI Interviews This Month"
                        used={interviewsUsed}
                        limit={planLimits.maxInterviewsPerMonth || 0}
                        unlimited={effectiveLimits?.unlimited?.interviews}
                        addon={usage?.addonInterviewCredits || 0}
                        onTopUp={() => setAddonOpen(true)}
                    />
                    <UsageRow
                        icon={Briefcase}
                        label="Active Jobs (Open Positions)"
                        used={jobsUsed}
                        limit={planLimits.maxActiveJobs || 0}
                        unlimited={effectiveLimits?.unlimited?.activeJobs}
                        addon={usage?.addonActiveJobCredits || 0}
                        onTopUp={() => setAddonOpen(true)}
                    />
                    <UsageRow
                        icon={Users}
                        label="Team Members"
                        used={membersUsed}
                        limit={planLimits.maxUsers || 0}
                        unlimited={effectiveLimits?.unlimited?.users}
                    />

                    {/* session breakdown */}
                    <div className="rounded-xl border border-border bg-card p-4 space-y-3">
                        <p className="text-sm font-semibold text-foreground">Interview Sessions</p>
                        <div className="grid grid-cols-2 gap-3">
                            {[
                                { label: 'Pending / In-Progress', value: usage?.scheduledSessions ?? 0, icon: Clock, cls: 'text-amber-500' },
                                { label: 'Completed This Month', value: usage?.completedSessionsThisMonth ?? 0, icon: CheckCircle2, cls: 'text-green-500' },
                            ].map(({ label, value, icon: Icon, cls }) => (
                                <div key={label} className="flex items-center gap-3 rounded-lg bg-muted/30 px-3 py-3">
                                    <Icon className={`h-5 w-5 shrink-0 ${cls}`} />
                                    <div>
                                        <p className="text-lg font-bold tabular-nums text-foreground">{value}</p>
                                        <p className="text-xs text-muted-foreground">{label}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Add-On pack CTA */}
                    <div className="flex items-center justify-between gap-4 rounded-xl border border-primary/25 bg-primary/5 px-5 py-4">
                        <div className="flex items-center gap-3">
                            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/15">
                                <Package className="h-4.5 w-4.5 text-primary" />
                            </div>
                            <div>
                                <p className="text-sm font-semibold text-foreground">Add-On Interview Pack</p>
                                <p className="text-xs text-muted-foreground">+100 interviews &amp; +2 active jobs · ₹6,000 · credits never expire</p>
                            </div>
                        </div>
                        <Button size="sm" variant="outline" className="shrink-0 border-primary/40 text-primary hover:bg-primary/10" onClick={() => setAddonOpen(true)}>
                            Request
                        </Button>
                    </div>
                </TabsContent>

                {/* ── Features Tab ── */}
                <TabsContent value="features" className="mt-4 space-y-3">
                    {plan ? (
                        <>
                            <div className="rounded-xl border border-border bg-card p-4 space-y-3">
                                <p className="text-sm font-semibold text-foreground flex items-center gap-2">
                                    <Shield className="h-4 w-4 text-primary" /> Core Capabilities
                                </p>
                                <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
                                    <FeatureItem label="AI-Powered Interviews" enabled />
                                    <FeatureItem label="Candidate Reports" enabled />
                                    {CAPABILITY_ITEMS.map(({ key, label, highlight }) => (
                                        <FeatureItem
                                            key={key}
                                            label={label}
                                            enabled={Boolean(planLimits[key])}
                                            highlight={highlight}
                                        />
                                    ))}
                                </div>
                            </div>

                            {/* upgrade nudge */}
                            <div className="flex items-center justify-between gap-4 rounded-xl border border-border bg-muted/20 px-5 py-4">
                                <div>
                                    <p className="text-sm font-medium text-foreground">Want more features?</p>
                                    <p className="text-xs text-muted-foreground mt-0.5">Upgrade your plan to unlock proctoring, advanced analytics, white-label, and more.</p>
                                </div>
                                <Button size="sm" className="shrink-0 gap-1.5" onClick={() => setUpgradeOpen(true)}>
                                    <TrendingUp className="h-3.5 w-3.5" /> Upgrade
                                </Button>
                            </div>
                        </>
                    ) : (
                        <div className="flex h-40 items-center justify-center rounded-xl border border-border bg-card text-muted-foreground text-sm">
                            No plan assigned — contact your account manager.
                        </div>
                    )}
                </TabsContent>

                {/* ── History Tab ── */}
                <TabsContent value="invoices" className="mt-4 space-y-4">
                    <MyBillingPanel />
                    <MyInvoicesPanel />
                </TabsContent>

                <TabsContent value="history" className="mt-4 space-y-4">
                    <UsageHistoryTable
                        fetcher={() => getMyUsageHistory()}
                        title="Usage by billing period"
                    />
                    {subscriptionLogs?.length > 0 ? (
                        <div className="rounded-xl border border-border bg-card divide-y divide-border overflow-hidden">
                            {subscriptionLogs.map((log, i) => {
                                const meta = ACTION_META[log.action] || { label: log.action, cls: 'bg-muted text-muted-foreground' };
                                return (
                                    <div key={log._id} className={`flex items-start gap-4 px-4 py-3.5 ${i === 0 ? 'bg-muted/20' : ''}`}>
                                        <div className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[10px] font-bold ${meta.cls}`}>
                                            {i + 1}
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <span className="text-sm font-semibold text-foreground">{meta.label}</span>
                                                {log.planId && (
                                                    <span className="flex items-center gap-1 text-xs text-muted-foreground">
                                                        <ChevronRight className="h-3 w-3" />
                                                        {log.planId.name}
                                                        {log.planId.price && (
                                                            <span className="text-primary">₹{log.planId.price.toLocaleString('en-IN')}/mo</span>
                                                        )}
                                                    </span>
                                                )}
                                                {i === 0 && (
                                                    <span className="rounded-full bg-primary/10 px-1.5 py-0.5 text-[10px] font-medium text-primary">Latest</span>
                                                )}
                                            </div>
                                            {log.previousPlanId && (
                                                <p className="text-xs text-muted-foreground">
                                                    From: {log.previousPlanId.name}
                                                </p>
                                            )}
                                            {log.notes && (
                                                <p className="mt-0.5 text-xs text-muted-foreground line-clamp-2 italic">{log.notes}</p>
                                            )}
                                        </div>
                                        <span className="shrink-0 text-xs text-muted-foreground whitespace-nowrap">{formatDate(log.createdAt)}</span>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="flex h-40 items-center justify-center rounded-xl border border-border bg-card text-sm text-muted-foreground">
                            No subscription history yet.
                        </div>
                    )}
                </TabsContent>
            </Tabs>

            <UpgradeRequestModal
                open={upgradeOpen}
                onClose={() => setUpgradeOpen(false)}
                currentPlanSlug={plan?.slug}
                lapsed={status === 'expired' || status === 'cancelled'}
            />
            <AddonRequestModal open={addonOpen} onClose={() => setAddonOpen(false)} />
        </div>
    );
}
